import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
  useTheme,
  useToast,
} from '@chakra-ui/react';
import { Redirect, Route, Switch } from 'react-router-dom';
import PageNotFound from './common/components/PageNotFound';
import RouteWithSubRoutes from './common/components/RouteWithSubRoutes';
import CenteredLoader from 'clipsal-cortex-ui/src/components/CenteredLoader';
import { unwrapResult } from '@reduxjs/toolkit';
import { checkUserLoggedIn, fetchUserDetails, selectUser } from './features/user/userSlice';
import { useAppDispatch } from './app/hooks';
import { useSelector } from 'react-redux';
import BarcodeScannerOverlay from './common/components/BarcodeScannerOverlay';
import BottomNav from './features/navigation/BottomNav';
import SideNav from './features/navigation/SideNav';
import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';
import { NAV_LINKS, routes } from './Routes';
import {
  ANDROID_PLAY_STORE_URL,
  APPLE_APP_STORE_URL,
  APP_VERSION,
  SIDENAV_WIDTH,
  TOP_NAV_SPACING_AFFORDANCE,
  IS_NOT_DEVELOPMENT,
} from './common/constants';
import { App as CapApp, URLOpenListenerEvent } from '@capacitor/app';
import { post } from './api/api-helpers';
import { VersionCheckResult } from './api/api-version-check-result';
import { Browser } from '@capacitor/browser';
import BusinessDetailsModal from './features/authentication/BusinessDetailsModal';
import UserDetailsModal from './features/user/UserDetailsModal';
import { createBrowserHistory } from 'history';
import LogRocket from 'logrocket';
import ReactGA from 'react-ga4';
import * as amplitude from '@amplitude/analytics-browser';
import { Router } from 'react-router-dom';
import useAppVisibility from 'clipsal-cortex-utils/src/hooks/use-app-visibility';
import PullToRefresh from './common/components/pull-to-refresh/PullToRefresh';

/** ------------------------------------ Amplitude Analytics Configuration ------------------------------------ */
const lastViewedDate = new Date();
// Updates each time the app is minimized then maximized, then set back to 0 on route change.
let timeSpendIdleMS = 0;

// Send all route changes to GA
const history = createBrowserHistory();
// Initial path on load
let previouslyVisitedPath = history.location.pathname.replace(/^\/site\/[0-9]+\//, '/site/<id>/');

if (IS_NOT_DEVELOPMENT) {
  history.listen((location) => {
    // replace the site ID with a placeholder
    const pathname = location.pathname.replace(/^\/site\/\d+\//, '/site/<id>/');

    const now = new Date();
    const differenceInMS = now.getTime() - lastViewedDate.getTime() - timeSpendIdleMS;
    lastViewedDate.setTime(now.getTime());

    timeSpendIdleMS = 0;

    // Required event properties for page views
    const eventProperties: Record<string, string> = {
      '[Amplitude] Page Path': pathname,
    };

    // If we have more properties to add, add them here
    if (window.location) {
      const { hostname, href, origin, host, pathname } = window.location;
      eventProperties['[Amplitude] Page Domain'] = hostname;
      eventProperties['[Amplitude] Page Location'] = href;
      eventProperties['[Amplitude] Page URL'] = href;
      eventProperties['Previous Path'] = previouslyVisitedPath;
      eventProperties['Seconds Spent on Previous Path'] = (differenceInMS / 1000).toString();
      eventProperties['LogRocket Session URL'] = LogRocket.sessionURL ?? 'NO_SESSION_AVAILABLE';
      eventProperties['referrer'] = origin + pathname;
      eventProperties['referring_domain'] = host;
    }

    ReactGA.send({ hitType: 'pageview', page: pathname });
    amplitude.logEvent('[Amplitude] Page Viewed', eventProperties);

    previouslyVisitedPath = pathname;
  });
}
/** ------------------------------------ End Amplitude Analytics Configuration ------------------------------------ */

function App() {
  const [isLoaded, setLoaded] = useState(false);
  const dispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const isMobileViewport = useBreakpointValue(
    {
      base: true,
      xl: false,
    },
    { ssr: false }
  );
  const shouldShowSidebar = !isMobileViewport && user.isLoggedIn;
  const toastRef = useRef<string | number>(0);
  const toast = useToast();
  const theme = useTheme();
  const backgroundColor = useColorModeValue('customBodyBackground.500', 'customBodyBackground.800');
  const isNativePlatform = Capacitor.isNativePlatform();
  const {
    isOpen: isBusinessDetailsModalOpen,
    onOpen: onOpenBusinessDetailsModal,
    onClose: onCloseBusinessDetailsModal,
  } = useDisclosure();
  const {
    isOpen: isUserDetailsModalOpen,
    onOpen: onOpenUserDetailsModal,
    onClose: onCloseUserDetailsModal,
  } = useDisclosure();
  // Used to track time spent on each page in amplitude, subtracting time the app is spent minimized
  const appMinimizedTimestamp = useRef<Date | null>(null);

  const handleAppVisibilityChange = useCallback((isVisible: boolean) => {
    if (isVisible && appMinimizedTimestamp.current) {
      const now = new Date();
      const timeSinceMinimizedMS = now.getTime() - appMinimizedTimestamp.current.getTime();
      timeSpendIdleMS += timeSinceMinimizedMS;
      appMinimizedTimestamp.current = now;
    } else {
      // App was minimized
      appMinimizedTimestamp.current = new Date();
    }
  }, []);

  useAppVisibility(handleAppVisibilityChange);

  useEffect(() => {
    const isDarkBackGround = backgroundColor === 'customBodyBackground.800';
    const backgroundHexColor = isDarkBackGround ? theme.colors.gray[900] : theme.colors.white;
    const statusBarTextStyle = isDarkBackGround ? 'Dark' : 'Light';

    if (isNativePlatform) {
      const setStatusBarStyle = async () => {
        await StatusBar.setStyle({ style: Style[statusBarTextStyle] });
        if (Capacitor.getPlatform() === 'android') {
          StatusBar.setBackgroundColor({ color: backgroundHexColor });
        }
      };
      setStatusBarStyle();
    }
  }, [backgroundColor, isNativePlatform, theme.colors]);

  useEffect(() => {
    const urlParams = new URLSearchParams(history.location.search);
    const banner = urlParams.get('banner');
    if (banner === 'rebranding') {
      toast({
        title: 'You have been redirected to our new domain!',
        description: 'Welcome to Clipsal Cortex Fleet!',
        status: 'info',
        isClosable: true,
      });
    }

    if (isNativePlatform) {
      CapApp.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
        // In IOS this is also fired during bill sharing through other apps or filesystem
        // so ensuring this function does not get invoked in such cases
        if (!event.url.includes('clipsalcortex.com')) return;

        const deepLinkUrl = event.url.split('.com').pop();
        if (deepLinkUrl) history.push(deepLinkUrl);
      });
    }
  }, []);

  useEffect(() => {
    // If the user is able to login with cognito but we do not have a record on db
    // Ask user for tenant details and complete signup process
    // NOTE: We get this error only when a user completes OTP verification but fails
    //       to complete signup
    if (user.isLoggedIn && user.loginError === 'USER_NOT_FOUND' && !user.tenant_id) onOpenBusinessDetailsModal();

    // Invited users might not have user details, prompt them to fill
    if (user.hasFetchedUserDetails && !user.user_first_name && !user.user_last_name && !user.loginError)
      onOpenUserDetailsModal();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.loginError, user.hasFetchedUserDetails]);

  useEffect(() => {
    async function checkIfUserIsLoggedIn() {
      const user = unwrapResult(await dispatch(checkUserLoggedIn()));

      if (user.isLoggedIn) {
        await dispatch(fetchUserDetails());
      }

      setLoaded(true);
    }

    checkIfUserIsLoggedIn();

    if (isNativePlatform) {
      const setStatusBarStyleLight = async () => {
        await StatusBar.setStyle({ style: Style.Light });
        if (Capacitor.getPlatform() === 'android') await StatusBar.setBackgroundColor({ color: theme.colors.white });
      };
      setStatusBarStyleLight();

      CapApp.addListener('backButton', ({ canGoBack }) => {
        if (!canGoBack) {
          CapApp.exitApp();
        } else {
          history.goBack();
        }
      });
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, history]);

  useEffect(() => {
    async function showNotificationIfUpdateRequired() {
      if (toastRef.current) return;

      const versionCheckResult = await post<VersionCheckResult>('/fleet/version_check', {
        platform: Capacitor.getPlatform().toUpperCase(),
        version: APP_VERSION,
      });

      if (versionCheckResult.update_required) {
        toastRef.current = toast({
          duration: null,
          render: () => (
            <Flex direction={'column'} alignItems={'center'} borderRadius={10} color="white" p={3} bg="green.500">
              <Flex mb={2} align={'center'}>
                <Heading mr={1} size={'sm'}>
                  A new version of Clipsal Cortex Fleet is available
                </Heading>
              </Flex>
              <Button
                width={'50%'}
                variant={'outline'}
                size={'xs'}
                onClick={async () => {
                  const url = Capacitor.getPlatform() === 'ios' ? APPLE_APP_STORE_URL : ANDROID_PLAY_STORE_URL;
                  await Browser.open({ url });
                }}
              >
                Update
              </Button>
            </Flex>
          ),
        }) as string | number;
      }
    }

    if (isNativePlatform) showNotificationIfUpdateRequired();
  }, [toast, isNativePlatform]);

  return (
    <Router history={history}>
      <PullToRefresh>
        <Flex bg={backgroundColor} maxWidth="100vw" minHeight="100vh" overflowX="hidden">
          {shouldShowSidebar && (
            <Box minWidth={SIDENAV_WIDTH}>
              <SideNav links={NAV_LINKS} />
            </Box>
          )}
          <Box minWidth={shouldShowSidebar ? `calc(100vw - ${SIDENAV_WIDTH})` : '100vw'}>
            {/* Exists to ensure the top area (e.g. the notch on iPhone X+) does not obfuscate content. */}
            <Box
              pt={isMobileViewport ? `calc(env(safe-area-inset-top) + ${TOP_NAV_SPACING_AFFORDANCE})` : 0}
              width={'100%'}
              zIndex={10}
              top={0}
              bg={backgroundColor}
            />
            <BarcodeScannerOverlay />

            {isLoaded ? (
              <Switch>
                <Redirect to={'/dashboard'} from="/" exact />
                {routes
                  .filter(({ requiresLogin }) => (requiresLogin && user.isLoggedIn) || !requiresLogin)
                  .map((route, i) => (
                    <RouteWithSubRoutes key={i} {...route} />
                  ))}
                <Route path="*">{user.isLoggedIn ? <PageNotFound /> : <Redirect to="/login" exact />}</Route>
              </Switch>
            ) : (
              <Box h={'100vh'}>
                <CenteredLoader />
              </Box>
            )}
          </Box>

          {isBusinessDetailsModalOpen && (
            <BusinessDetailsModal {...{ isOpen: isBusinessDetailsModalOpen, onClose: onCloseBusinessDetailsModal }} />
          )}
          {isUserDetailsModalOpen && (
            <UserDetailsModal {...{ isOpen: isUserDetailsModalOpen, onClose: onCloseUserDetailsModal }} />
          )}
          {isMobileViewport && <BottomNav links={NAV_LINKS} />}
        </Flex>
      </PullToRefresh>
    </Router>
  );
}

export default App;
